import React, { useState, useEffect } from "react";
import {
  Container,
  Typography,
  CircularProgress,
  Card,
  CardContent,
  CardMedia,
  Box,
  Button,
  Chip,
} from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import { db, auth, storage } from "../../../firebase-config";
import { doc, getDoc, deleteDoc } from "firebase/firestore";
import { ref, deleteObject } from "firebase/storage";

const AdminProductView = () => {
  const { id: productId } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const navigate = useNavigate();
  const currentUser = auth.currentUser;

  useEffect(() => {
    if (!currentUser) return;

    const fetchProduct = async () => {
      try {
        const productRef = doc(db, "products", productId);
        const productSnap = await getDoc(productRef);
        if (productSnap.exists()) {
          setProduct(productSnap.data());
        } else {
          setError("Product not found.");
        }
      } catch (err) {
        console.error("Error fetching product:", err);
        setError("Failed to load product.");
      } finally {
        setLoading(false);
      }
    };

    const checkAdminStatus = async () => {
      try {
        const adminRef = doc(db, "admins", currentUser.uid);
        const adminSnap = await getDoc(adminRef);
        setIsAdmin(adminSnap.exists());
      } catch (err) {
        console.error("Error checking admin status:", err);
        setError("Failed to verify admin access.");
      }
    };

    fetchProduct();
    checkAdminStatus();
  }, [currentUser, productId]);

  const handleDeleteProduct = async () => {
    if (!isAdmin) return;

    const confirmDelete = window.confirm("Are you sure you want to delete this product?");
    if (!confirmDelete) return;

    try {
      // Delete product image from storage if it exists
      if (product.imageUrl) {
        const imageRef = ref(storage, product.imageUrl);
        await deleteObject(imageRef).catch((err) => console.warn("Image deletion error:", err));
      }

      // Delete product document from Firestore
      const productRef = doc(db, "products", productId);
      await deleteDoc(productRef);

      alert("Product deleted successfully!");
      navigate("/admin/products");
    } catch (err) {
      console.error("Error deleting product:", err);
      setError("Failed to delete product.");
    }
  };

  if (loading) return <CircularProgress />;
  if (error) return <Typography color="error">{error}</Typography>;

  return (
    <Container>
      <Typography variant="h4" gutterBottom>
        Product Details
      </Typography>

      <Card sx={{ mb: 3 }}>
        <CardMedia
          component="img"
          height="300"
          image={product.imageUrl || "https://via.placeholder.com/300"}
          alt={product.name}
        />
        <CardContent>
          <Typography variant="h5">{product.name}</Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
            {product.description || "No description available."}
          </Typography>
          <Typography variant="h6" sx={{ mt: 2 }}>
            Price: ${product.defaultPrice ? product.defaultPrice.toFixed(2) : "N/A"}
          </Typography>
          <Chip label={product.category} color="primary" sx={{ mt: 1 }} />

          {isAdmin && (
            <Box sx={{ mt: 3 }}>
              <Button
                variant="contained"
                color="primary"
                sx={{ mr: 2 }}
                onClick={() => navigate(`/admin/products/edit/${productId}`)}
              >
                Edit Product
              </Button>
              <Button variant="contained" color="error" onClick={handleDeleteProduct}>
                Delete Product
              </Button>
            </Box>
          )}
        </CardContent>
      </Card>

      <Button variant="outlined" onClick={() => navigate("/admin/products")}>
        Back to Products
      </Button>
    </Container>
  );
};

export default AdminProductView;
